import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_URL from '../../config/api';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const COLORS = ['#2563eb', '#1e40af', '#334e68', '#16a34a', '#dc2626', '#ea580c', '#8b5cf6', '#ec4899'];

const Statistics = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);

  const fetchStatistics = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/api/quality-records/statistics/?days=${days}`
      );
      setStats(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques:', error);
      console.error('Détails:', error.response?.data || error.message);
      setLoading(false);
      // Afficher un message d'erreur à l'utilisateur
      alert(`Erreur lors du chargement des statistiques: ${error.response?.data?.detail || error.message || 'Erreur inconnue'}`);
    }
  }, [days]);

  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  const exportExcel = async () => {
    try {
      setExportingExcel(true);
      const response = await axios.get(
        `${API_URL}/api/quality-records/export_excel/`,
        { responseType: 'blob', timeout: 60000 }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `quality_records_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setExportingExcel(false);
    } catch (error) {
      console.error('Erreur lors de l\'export Excel:', error);
      alert('Erreur lors de l\'export Excel: ' + (error.response?.data?.message || error.message));
      setExportingExcel(false);
    }
  };

  const exportPDF = async () => {
    try {
      setExportingPDF(true);
      const response = await axios.get(
        `${API_URL}/api/quality-records/export_pdf/?days=${days}`,
        { responseType: 'blob', timeout: 60000 }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `quality_report_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setExportingPDF(false);
    } catch (error) {
      console.error('Erreur lors de l\'export PDF:', error);
      alert('Erreur lors de l\'export PDF: ' + (error.response?.data?.message || error.message));
      setExportingPDF(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="loading-spinner h-12 w-12 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des statistiques...</p>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="card text-center py-12">
        <div className="text-5xl mb-4">⚠️</div>
        <h3 className="text-xl font-bold text-red-600 mb-2">Erreur</h3>
        <p className="text-gray-600">Erreur lors du chargement des statistiques</p>
      </div>
    );
  }

  // Get all lines sorted by count (not just top 5)
  const allLines = stats.by_line ? [...stats.by_line].sort((a, b) => b.count - a.count) : [];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header avec actions */}
      <div className="bg-gradient-primary rounded-xl shadow-corporate-xl p-6 sm:p-8 text-white">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold mb-2">Statistiques Avancées</h2>
            <p className="text-white/80 text-sm sm:text-base">Analyse complète des données de contrôle qualité</p>
          </div>
          <div className="flex flex-wrap gap-3 w-full lg:w-auto">
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="input-modern w-full lg:w-auto min-w-[160px] bg-white/10 border-white/20 text-white placeholder-white/70 focus:bg-white/20"
            >
              <option value={7} className="text-gray-900">7 derniers jours</option>
              <option value={30} className="text-gray-900">30 derniers jours</option>
              <option value={90} className="text-gray-900">90 derniers jours</option>
              <option value={365} className="text-gray-900">1 an</option>
            </select>
            <button
              onClick={exportExcel}
              disabled={exportingExcel}
              className="btn-secondary bg-white/10 hover:bg-white/20 border-white/30 text-white disabled:opacity-50 flex items-center space-x-2"
            >
              {exportingExcel ? (
                <>
                  <div className="loading-spinner h-4 w-4 border-white"></div>
                  <span>Export...</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Excel</span>
                </>
              )}
            </button>
            <button
              onClick={exportPDF}
              disabled={exportingPDF}
              className="btn-secondary bg-white/10 hover:bg-white/20 border-white/30 text-white disabled:opacity-50 flex items-center space-x-2"
            >
              {exportingPDF ? (
                <>
                  <div className="loading-spinner h-4 w-4 border-white"></div>
                  <span>Export...</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <span>PDF</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Cartes de résumé - Total Retouches et Tendance au milieu (50% chacun) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 max-w-4xl mx-auto">
        <div className="card bg-gradient-primary text-white border-0 shadow-corporate-lg">
          <div className="flex items-center justify-between mb-3">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
          </div>
          <p className="text-sm font-medium text-white/80 mb-1">Total Retouches</p>
          <p className="text-3xl sm:text-4xl font-bold mb-2">{stats.total || 0}</p>
          {stats.avg_per_day && (
            <p className="text-xs text-white/70">Moyenne: {stats.avg_per_day.toFixed(1)}/jour</p>
          )}
        </div>

        {stats.trend !== undefined && (
          <div className="card bg-corporate-700 text-white border-0 shadow-corporate-lg">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                {stats.trend > 0 ? (
                  <svg className="w-6 h-6 text-red-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                ) : stats.trend < 0 ? (
                  <svg className="w-6 h-6 text-green-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14" />
                  </svg>
                )}
              </div>
            </div>
            <p className="text-sm font-medium text-white/80 mb-1">Tendance</p>
            <div className="flex items-center space-x-2">
              <span className={`text-2xl sm:text-3xl font-bold ${stats.trend > 0 ? 'text-red-200' : stats.trend < 0 ? 'text-green-200' : 'text-white'}`}>
                {stats.trend > 0 ? '+' : ''}{stats.trend}%
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Graphiques améliorés */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {stats.by_line && stats.by_line.length > 0 && (
          <div className="card border-0 shadow-corporate-lg">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-corporate-900">Retouches par Chaîne</h3>
              <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stats.by_line}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="production_line__name" tick={{ fill: '#6b7280', fontSize: 11 }} />
                <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    fontSize: '13px'
                  }}
                />
                <Bar dataKey="count" fill="#2563eb" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {stats.by_retouch && stats.by_retouch.length > 0 && (
          <div className="card border-0 shadow-corporate-lg">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-corporate-900">Répartition par Type</h3>
              <div className="w-10 h-10 bg-accent-50 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-accent-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
                </svg>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={stats.by_retouch}
                  dataKey="count"
                  nameKey="retouch_type__name"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                >
                  {stats.by_retouch.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    fontSize: '13px'
                  }}
                  formatter={(value, name, props) => [
                    `${value} retouches`,
                    'Nombre'
                  ]}
                />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                  formatter={(value, entry) => {
                    // Trouver l'entrée correspondante pour obtenir le nom complet
                    const dataEntry = stats.by_retouch.find(item => item.retouch_type__name === value);
                    return dataEntry ? `${value} (${dataEntry.count})` : value;
                  }}
                  iconType="circle"
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Graphique d'évolution */}
      {stats.evolution && stats.evolution.length > 0 && (
        <div className="card border-0 shadow-corporate-lg">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-corporate-900">Évolution dans le Temps</h3>
            <div className="w-10 h-10 bg-corporate-50 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-corporate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={stats.evolution}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="day" tick={{ fill: '#6b7280', fontSize: 11 }} />
              <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Line
                type="monotone"
                dataKey="count"
                stroke="#2563eb"
                strokeWidth={3}
                dot={{ r: 5, fill: '#2563eb' }}
                activeDot={{ r: 8 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Distribution horaire */}
      {stats.hourly_distribution && stats.hourly_distribution.length > 0 && (
        <div className="card border-0 shadow-corporate-lg">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-corporate-900">Distribution par Heure</h3>
            <div className="w-10 h-10 bg-corporate-50 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-corporate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={stats.hourly_distribution}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="hour" tick={{ fill: '#6b7280', fontSize: 11 }} />
              <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Bar dataKey="count" fill="#334e68" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Nouveaux graphiques supplémentaires */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Graphique par Modèle */}
        {stats.by_model && stats.by_model.length > 0 && (
          <div className="card border-0 shadow-corporate-lg">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-corporate-900">Retouches par Modèle</h3>
              <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stats.by_model.slice(0, 10)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" tick={{ fill: '#6b7280', fontSize: 11 }} />
                <YAxis dataKey="model__name" type="category" width={100} tick={{ fill: '#6b7280', fontSize: 10 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    fontSize: '13px'
                  }}
                />
                <Bar dataKey="count" fill="#16a34a" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Top Retouches - Graphique en barres horizontales */}
        {stats.top_retouches && stats.top_retouches.length > 0 && (
          <div className="card border-0 shadow-corporate-lg">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-corporate-900">Top Types de Retouches</h3>
              <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stats.top_retouches} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" tick={{ fill: '#6b7280', fontSize: 11 }} />
                <YAxis dataKey="retouch_type__name" type="category" width={120} tick={{ fill: '#6b7280', fontSize: 10 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    fontSize: '13px'
                  }}
                />
                <Bar dataKey="count" fill="#8b5cf6" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Graphique comparatif Chaînes vs Modèles */}
      {stats.by_line && stats.by_model && stats.by_line.length > 0 && stats.by_model.length > 0 && (() => {
        // Préparer les données avec un champ "type" pour distinguer chaînes et modèles
        const lineData = stats.by_line.slice(0, 5).map(item => ({
          ...item,
          name: item.production_line__name,
          type: 'Chaîne',
          typeLabel: 'Chaînes de production'
        }));
        const modelData = stats.by_model.slice(0, 5).map(item => ({
          ...item,
          name: item.model__name,
          type: 'Modèle',
          typeLabel: 'Modèles'
        }));
        const combinedData = [...lineData, ...modelData];
        
        return (
          <div className="card border-0 shadow-corporate-lg">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-corporate-900">Comparaison Chaînes vs Modèles</h3>
              <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
            </div>
            {/* Légende personnalisée */}
            <div className="flex items-center justify-center gap-6 mb-4">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-[#ea580c]"></div>
                <span className="text-sm font-medium text-gray-700">Chaînes de production</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-[#f97316]"></div>
                <span className="text-sm font-medium text-gray-700">Modèles</span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={combinedData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="name" 
                  tick={{ fill: '#6b7280', fontSize: 10 }} 
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}
                  formatter={(value, name, props) => [
                    `${value} retouches`,
                    props.payload.typeLabel
                  ]}
                  labelFormatter={(label) => `Nom: ${label}`}
                />
                <Bar 
                  dataKey="count" 
                  radius={[8, 8, 0, 0]}
                >
                  {combinedData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={entry.type === 'Chaîne' ? '#ea580c' : '#f97316'} 
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        );
      })()}

      {/* Classements - TOUS les éléments */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {allLines.length > 0 && (
          <div className="card border-0 shadow-corporate-lg">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-corporate-900">Classement des Chaînes</h3>
              <div className="px-3 py-1 bg-primary-50 rounded-lg">
                <span className="text-xs font-semibold text-primary-700">{allLines.length} chaîne{allLines.length > 1 ? 's' : ''}</span>
              </div>
            </div>
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {allLines.map((line, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-white rounded-lg hover:from-primary-50 hover:to-primary-50 transition-all duration-200 border border-gray-100 hover:border-primary-200"
                >
                  <div className="flex items-center space-x-4 flex-1 min-w-0">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-white text-sm shadow-corporate flex-shrink-0 ${
                      idx === 0 ? 'bg-gradient-to-br from-yellow-400 to-yellow-600' :
                      idx === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-500' :
                      idx === 2 ? 'bg-gradient-to-br from-orange-400 to-orange-600' :
                      'bg-gradient-to-br from-corporate-500 to-corporate-700'
                    }`}>
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-corporate-900 text-sm truncate">{line.production_line__name}</p>
                      <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                        <div 
                          className={`h-2 rounded-full ${
                            idx === 0 ? 'bg-gradient-to-r from-yellow-400 to-yellow-600' :
                            idx === 1 ? 'bg-gradient-to-r from-gray-300 to-gray-500' :
                            idx === 2 ? 'bg-gradient-to-r from-orange-400 to-orange-600' :
                            'bg-gradient-to-r from-primary-500 to-primary-600'
                          }`}
                          style={{ width: `${(line.count / allLines[0].count) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  <div className="ml-4 flex-shrink-0">
                    <span className="px-4 py-2 bg-primary-600 text-white rounded-lg font-bold text-sm shadow-corporate">
                      {line.count}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {stats.by_worker && stats.by_worker.length > 0 && (
          <div className="card border-0 shadow-corporate-lg">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-corporate-900">Classement des Ouvriers</h3>
              <div className="px-3 py-1 bg-accent-50 rounded-lg">
                <span className="text-xs font-semibold text-accent-700">{stats.by_worker.length} ouvrier{stats.by_worker.length > 1 ? 's' : ''}</span>
              </div>
            </div>
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {stats.by_worker.map((worker, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-white rounded-lg hover:from-accent-50 hover:to-accent-50 transition-all duration-200 border border-gray-100 hover:border-accent-200"
                >
                  <div className="flex items-center space-x-4 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-accent-500 to-accent-700 flex items-center justify-center font-bold text-white text-sm shadow-corporate flex-shrink-0">
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-corporate-900 text-sm truncate">{worker.worker__username || 'Anonyme'}</p>
                      <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                        <div 
                          className="h-2 rounded-full bg-gradient-to-r from-accent-500 to-accent-600"
                          style={{ width: `${stats.by_worker[0].count > 0 ? (worker.count / stats.by_worker[0].count) * 100 : 0}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  <div className="ml-4 flex-shrink-0">
                    <span className="px-4 py-2 bg-accent-600 text-white rounded-lg font-bold text-sm shadow-corporate">
                      {worker.count}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Statistics;
